import React from 'react';
import { motion } from 'framer-motion';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import SEO from '../components/SEO';
import { useLanguage } from '@/i18n/LanguageProvider';

const defaultTeamImage = 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&q=80';
const defaultVideoUrl = 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4';

export default function Studio() {
  const { t } = useLanguage();
  const { data: contents = [] } = useQuery({
    queryKey: ['studio-content'],
    queryFn: () => base44.entities.StudioContent.list(),
    initialData: []
  });

  const getContent = (key) => {
    return contents.find(c => c.key === key)?.content;
  };

  const teamImage = getContent('team_image');
  const videoUrl = getContent('video_url');
  return (
    <>
      <SEO 
        title="Studio"
        description={t('Bellomonte & Pensabene - Studio di architettura e interior design fondato nel 2006 a Palermo. Team multidisciplinare specializzato in progetti su misura.')}
        keywords="studio architettura Palermo, team design, architettura personalizzata, interior design team"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'Studio',
          url: 'https://studioarchitetturabellomontepensabene.it/Studio'
        }}
      />
      <div className="pt-20 md:pt-32">
      {/* Hero Section */}
      <section className="mx-auto max-w-[1800px] px-6 py-8 md:px-12 md:py-20 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-light text-[#2d2d2d] mb-8">
            Studio
          </h1>
          <div className="w-16 h-px bg-[#74675a] mb-8" />
        </motion.div>
      </section>

      {/* Team Section */}
      {teamImage && (
        <section className="bg-[#f8f8f8] py-12 md:py-24">
          <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="aspect-[4/3] overflow-hidden rounded-lg">
                  <img
                    key={teamImage}
                    src={teamImage}
                    alt={t('Il nostro team')}
                    width="1200"
                    height="900"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-xs tracking-widest-custom uppercase text-[#74675a] mb-6">
                  {t('Il Team')}
                </h2>
                <div className="space-y-5 text-[17px] leading-[1.75] text-[#5f5f5f] md:text-base">
                  <p>
                    {t("L'identità stilistica dello Studio Bellomonte & Pensabene, fondato nel 2006, si distingue per coerenza e riconoscibilità: spazi razionali e funzionali si fondono con una spiccata sensibilità estetica, arricchita dalle esperienze maturate all'estero.")}
                  </p>
                  <p>
                    {t("I volumi, modellati con precisione quasi scultorea, dialogano con luce naturale e artificiale per valorizzare forme e superfici. Materiali e finiture, selezionati con cura, definiscono ambienti dal carattere unico, in cui anche l'arredo diventa parte integrante dell'architettura, contribuendo a un linguaggio armonico e compiuto.")}
                  </p>
                  <p>
                    {t("Ogni cliente è unico, e unico deve essere lo spazio che lo accoglie. L'ascolto è il punto di partenza di un processo che trasforma esigenze e desideri in soluzioni eleganti, funzionali e personali. Progettiamo ambienti capaci di raccontare una storia autentica e migliorare la qualità della vita.")}
                  </p>
                  <p>
                    {t('Il cuore operativo dello studio è un team multidisciplinare che lavora in sinergia, garantendo qualità e coerenza dal concept alla realizzazione.')}
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Video Section */}
      {videoUrl && (
        <section className="py-16 md:py-24">
          <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="max-w-xs mx-auto"
            >
              <div className="aspect-[9/16] overflow-hidden rounded-lg bg-black">
                <video
                  controls
                  className="w-full h-full object-contain"
                  src={videoUrl}
                >
                  {t('Il tuo browser non supporta il tag video.')}
                </video>
              </div>
            </motion.div>
          </div>
        </section>
      )}
    </div>
    </>
  );
}